import React, { useRef, useCallback, useEffect, useState } from 'react';
import { FiLock, FiMail, FiCheck, FiX } from 'react-icons/fi';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';
import { useHistory, useParams } from 'react-router-dom';
import { useToast } from '../../../hooks/toast';
import logoImg from '../../../assets/login-logo.png';
import api from '../../../services/api';

import getValidationErrors from '../../../utils/getValidationErrors';
import Input from '../../../components/Inputs/InputGrande';
import BotaoLogin from '../../../components/Botoes/BotaoLogin';

import {
  Container,
  Content,
  Background,
  AnimationContainer,
  RequisitosSenha,
  RequisitoTitulo,
  Requisito,
} from './styles';

interface SignInFormData {
  email: string;
  password: string;
  password_confirmation: string;
}

interface IRouteParams {
  confirm?: string;
}

interface IMensagensErro {
  [campo: string]: string | undefined;
}

const MINIMO_CARACTERES_SENHA = 8;

const schemaSenha = Yup.object().shape({
  password: Yup.string()
    .required('A senha é obrigatória')
    .min(
      MINIMO_CARACTERES_SENHA,
      `A senha deve ter no mínimo ${MINIMO_CARACTERES_SENHA} caracteres`,
    ),
  password_confirmation: Yup.string()
    .required('A confirmação da senha é obrigatória')
    .oneOf([Yup.ref('password')], 'A senha informada não confere'),
});

const RedefinirSenha: React.FC<IRouteParams> = () => {
  const formRef = useRef<FormHandles>(null);
  const { confirm } = useParams<IRouteParams>();
  const { addToast } = useToast();
  const history = useHistory();
  const [loading_message, setLoadingMessage] = useState('');
  const [password, setPassword] = useState('');
  const [password_confirmation, setPasswordConfirmation] = useState('');
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true; // Will set it to true on mount ...
    return () => { mounted.current = false; }; // ... and to false on unmount
  }, []);

  useEffect(() => {
    async function getEmail(): Promise<void> {
      try {
        const response = await api.get(`auth/find-email?token=${confirm}`);
        formRef.current?.setFieldValue('email', response.data.email);
      } catch (err) {
        /*   addToast({
          type: 'error',
          title: 'Erro',
          description: 'Esse link não é válido ou expirou.',
        }); */
        history.push('/login');
      }
    }
    getEmail();
  }, [confirm, history]);

  const requisitos = [
    {
      texto: `Ter no mínimo ${MINIMO_CARACTERES_SENHA} caracteres`,
      atendido: password.length >= MINIMO_CARACTERES_SENHA,
    },
    {
      texto: 'A senha e a confirmação devem ser iguais',
      atendido: !!password && password === password_confirmation,
    },
  ];

  const atualizarMensagens = useCallback(async () => {
    const erros_atuais: IMensagensErro = formRef.current?.getErrors() ?? {};
    const campos_com_erro = Object.keys(erros_atuais).filter(
      campo => !!erros_atuais[campo],
    );

    // As mensagens só são atualizadas para os campos que já as estão exibindo
    if (!campos_com_erro.length) {
      return;
    }

    try {
      await schemaSenha.validate(formRef.current?.getData() ?? {}, {
        abortEarly: false,
      });
      formRef.current?.setErrors({});
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err);
        formRef.current?.setErrors(
          campos_com_erro.reduce<Record<string, string>>(
            (acumulado, campo) =>
              errors[campo]
                ? { ...acumulado, [campo]: errors[campo] }
                : acumulado,
            {},
          ),
        );
      }
    }
  }, []);

  const handleSubmit = useCallback(
    async (data: SignInFormData) => {
      try {
        setLoadingMessage('Enviando...');
        formRef.current?.setErrors({});

        await schemaSenha.validate(data, {
          abortEarly: false,
        });

        const response = await api.post('/auth/new-password', data, {
          headers: { confirm },
        });
        if(mounted.current){
          addToast({
            type: 'success',
            title: 'Senha alterada com sucesso',
            description: response.data.message,
          });
        }

        setTimeout(() => history.push('/login'), 3000);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
          return;
        }

        addToast({
          type: 'error',
          title: 'Erro no cadastro',
          description: 'Não foi possível concluir sua solicitação',
        });
      } finally {
        setLoadingMessage('');
      }
    },
    [addToast, confirm, history],
  );

  return (
    <Container>
      <Content>
        <AnimationContainer>
          <img src={logoImg} alt="BeerSales" />
          <Form ref={formRef} onSubmit={handleSubmit}>
            <Input
              name="email"
              maxLength={255}
              type="email"
              disabled
              icon={FiMail}
              placeholder="Informe o e-mail"
              showErrorMessage
            />
            <Input
              name="password"
              maxLength={255}
              icon={FiLock}
              autoComplete="false"
              type="password"
              placeholder="Informe a senha"
              showErrorMessage
              onChange={event => {
                setPassword(event.target.value);
                atualizarMensagens();
              }}
            />
            <Input
              name="password_confirmation"
              maxLength={255}
              icon={FiLock}
              autoComplete="false"
              type="password"
              placeholder="Confirme a senha"
              showErrorMessage
              onChange={event => {
                setPasswordConfirmation(event.target.value);
                atualizarMensagens();
              }}
            />

            <RequisitoTitulo>A senha deve:</RequisitoTitulo>
            <RequisitosSenha>
              {requisitos.map(requisito => (
                <Requisito key={requisito.texto} atendido={requisito.atendido}>
                  {requisito.atendido ? <FiCheck size={14} /> : <FiX size={14} />}
                  {requisito.texto}
                </Requisito>
              ))}
            </RequisitosSenha>

            <BotaoLogin loading={loading_message} type="submit">
              Redefinir senha
            </BotaoLogin>
          </Form>
        </AnimationContainer>
      </Content>
      <Background />
    </Container>
  );
};

export default RedefinirSenha;
